// ─── File / image attach plugin ───────────────────────────────────────────────
//
// Handles attaching images AND arbitrary files to a chat message. Two sides:
//   • COMPOSER  — useFileAttach() + <AttachmentBar/>: pick / paste / drag-drop a
//                 file, upload it to the chat's whimer uploads dir, show a chip.
//                 On send, buildMessageWithAttachments() appends a hidden marker
//                 per attachment carrying its ABSOLUTE server path so the agent can
//                 read the file locally (it runs on the same machine).
//   • RENDER    — extractImageMarkers() / extractFileMarkers() strip those markers
//                 out of a message so the raw path never shows in the chat, and the
//                 relevant component renders thumbnails / badges instead.
//
// Marker format:
//   [Attached image: /abs/path]   — for image/* files (renders as thumbnail)
//   [Attached file: /abs/path]    — for all other files (renders as name badge)
//
// Both are intentionally invisible in the chat bubble — user sees thumbnail or chip.

import { useCallback, useState } from 'react'
import { getApiUrl } from '../constants/config'
import { api } from '../api/client'
import { Icon } from '../components/Icon'

// ─── Markers ──────────────────────────────────────────────────────────────────

const IMAGE_MARKER_RE = /\[Attached image:\s*([^\]\n]+)\]/g
const FILE_MARKER_RE  = /\[Attached file:\s*([^\]\n]+)\]/g

export interface ReadyAttachment {
  path: string
  isImage: boolean
}

export function buildMessageWithAttachments(text: string, ready: ReadyAttachment[]): string {
  if (ready.length === 0) return text
  const markers = ready.map(a =>
    a.isImage ? `[Attached image: ${a.path}]` : `[Attached file: ${a.path}]`,
  ).join('\n')
  return text.trim() ? `${text.trim()}\n\n${markers}` : markers
}

/** Pull image-marker paths out of a message; return them + the message without markers. */
export function extractImageMarkers(content: string): { paths: string[]; clean: string } {
  const paths: string[] = []
  let m: RegExpExecArray | null
  IMAGE_MARKER_RE.lastIndex = 0
  while ((m = IMAGE_MARKER_RE.exec(content)) !== null) {
    const p = m[1].trim()
    if (p.startsWith('/')) paths.push(p)  // only real absolute paths, not placeholders/templates
  }
  const clean = content.replace(IMAGE_MARKER_RE, '').replace(/\n{3,}/g, '\n\n').trim()
  return { paths, clean }
}

/** Pull file-marker paths out of a message; return them + the message without markers. */
export function extractFileMarkers(content: string): { paths: string[]; clean: string } {
  const paths: string[] = []
  let m: RegExpExecArray | null
  FILE_MARKER_RE.lastIndex = 0
  while ((m = FILE_MARKER_RE.exec(content)) !== null) paths.push(m[1].trim())
  const clean = content.replace(FILE_MARKER_RE, '').replace(/\n{3,}/g, '\n\n').trim()
  return { paths, clean }
}

// ─── Thumbnail / badge rendering ──────────────────────────────────────────────

function thumbUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path
  const jwt = localStorage.getItem('whimer_jwt') ?? ''
  return `${getApiUrl()}/api/uploads?path=${encodeURIComponent(path)}&token=${encodeURIComponent(jwt)}`
}

/** Inline image(s) for a message, with tap-to-fullscreen. Used by renderContent. */
export function ImageThumbnails({ paths }: { paths: string[] }) {
  const [zoom, setZoom] = useState<string | null>(null)
  if (paths.length === 0) return null
  return (
    <>
      <div className="img-attach-grid">
        {paths.map((p, i) => (
          <button key={i} type="button" className="img-attach-thumb" onClick={() => setZoom(thumbUrl(p))}>
            <img src={thumbUrl(p)} alt="attached image" loading="lazy" />
          </button>
        ))}
      </div>
      {zoom && (
        <div className="img-attach-lightbox" onClick={() => setZoom(null)} role="dialog" aria-modal="true">
          <img src={zoom} alt="attached image" />
        </div>
      )}
    </>
  )
}

/** Inline file badge(s) for a message. Used by renderContent. */
export function FileBadges({ paths }: { paths: string[] }) {
  if (paths.length === 0) return null
  return (
    <div className="file-badge-list">
      {paths.map((p, i) => (
        <div key={i} className="file-badge">
          <Icon name="file-text" size={14} />
          <span>{p.split('/').pop() ?? p}</span>
        </div>
      ))}
    </div>
  )
}

// ─── Composer hook ─────────────────────────────────────────────────────────────

export interface Attachment {
  id:       string
  name:     string
  localUrl: string | null   // object URL for image preview; null for non-images
  isImage:  boolean
  status:   'uploading' | 'done' | 'error'
  path?:    string          // absolute server path once uploaded
  error?:   string
}

function readAsBase64(file: File): Promise<{ base64: string; mime: string }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = String(reader.result || '')
      const comma = result.indexOf(',')
      resolve({ base64: comma >= 0 ? result.slice(comma + 1) : result, mime: file.type || 'application/octet-stream' })
    }
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })
}

export function useFileAttach(chatId: string) {
  const [attachments, setAttachments] = useState<Attachment[]>([])

  const addFiles = useCallback((files: FileList | File[]) => {
    const list = Array.from(files)
    for (const file of list) {
      const id      = `att-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
      const isImage = file.type.startsWith('image/')
      const localUrl = isImage ? URL.createObjectURL(file) : null
      setAttachments(prev => [...prev, { id, name: file.name, localUrl, isImage, status: 'uploading' }])
      readAsBase64(file)
        .then(({ base64, mime }) => api.chats.uploadImage(chatId, { name: file.name, mime, base64 }))
        .then(({ path }) =>
          setAttachments(prev => prev.map(a => a.id === id ? { ...a, status: 'done', path } : a)),
        )
        .catch(err =>
          setAttachments(prev => prev.map(a => a.id === id ? { ...a, status: 'error', error: String(err?.message ?? err) } : a)),
        )
    }
  }, [chatId])

  const removeAttachment = useCallback((id: string) => {
    setAttachments(prev => {
      const a = prev.find(x => x.id === id)
      if (a?.localUrl) URL.revokeObjectURL(a.localUrl)
      return prev.filter(x => x.id !== id)
    })
  }, [])

  const clear = useCallback(() => {
    setAttachments(prev => { prev.forEach(a => { if (a.localUrl) URL.revokeObjectURL(a.localUrl) }); return [] })
  }, [])

  const uploading = attachments.some(a => a.status === 'uploading')
  const ready: ReadyAttachment[] = attachments
    .filter(a => a.status === 'done' && a.path)
    .map(a => ({ path: a.path as string, isImage: a.isImage }))

  return { attachments, addFiles, removeAttachment, clear, uploading, ready }
}

// Keep old export name so callers that use useImageAttach don't break.
export { useFileAttach as useImageAttach }

// ─── Composer attachment chips ─────────────────────────────────────────────────

export function AttachmentBar({
  attachments,
  onRemove,
}: {
  attachments: Attachment[]
  onRemove: (id: string) => void
}) {
  if (attachments.length === 0) return null
  return (
    <div className="att-bar">
      {attachments.map(a => (
        <div key={a.id} className={`att-chip att-chip--${a.status}${a.isImage ? '' : ' att-chip--file'}`}>
          {a.isImage && a.localUrl
            ? <img src={a.localUrl} alt={a.name} />
            : (
              <div className="att-chip-file-icon">
                <Icon name="file-text" size={22} />
                <span className="att-chip-file-name">{a.name}</span>
              </div>
            )
          }
          {a.status === 'uploading' && <span className="att-chip-spin">◌</span>}
          {a.status === 'error' && <span className="att-chip-err" title={a.error}>!</span>}
          <button type="button" className="att-chip-remove" onClick={() => onRemove(a.id)} aria-label="Remove attachment">
            <Icon name="x" size={12} />
          </button>
        </div>
      ))}
    </div>
  )
}
